import { useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';

const MAX_SLOTS = 5;
const DEFAULT_SLOTS = 1;

export function useToggleCellAvailability() {
  return useMutation(api.gardens.mutations.toggleCellAvailability).withOptimisticUpdate(
    (localStore, { gardenId, row, col }) => {
      const current = localStore.getQuery(api.gardens.queries.getGardenById, { gardenId });
      if (current === undefined) return;

      const existing = current.availableCells || [];
      const isAvailable = existing.some((c) => c.row === row && c.col === col);
      const nextAvailableCells = isAvailable
        ? existing.filter((c) => !(c.row === row && c.col === col))
        : [...existing, { row, col }];

      localStore.setQuery(
        api.gardens.queries.getGardenById,
        { gardenId },
        { ...current, availableCells: nextAvailableCells }
      );
    }
  );
}

export function useUpdateGardenSize() {
  return useMutation(api.gardens.mutations.updateGardenSize).withOptimisticUpdate(
    (localStore, { gardenId, rows, columns }) => {
      const current = localStore.getQuery(api.gardens.queries.getGardenById, { gardenId });
      if (current === undefined) return;

      const validPlants = current.plants.filter((p) => p.row < rows && p.col < columns);
      const validAvailableCells = (current.availableCells ?? []).filter(
        (c) => c.row < rows && c.col < columns
      );
      const validCellSettings = (current.cellSettings ?? []).filter(
        (c) => c.row < rows && c.col < columns
      );

      localStore.setQuery(
        api.gardens.queries.getGardenById,
        { gardenId },
        {
          ...current,
          rows,
          columns,
          plants: validPlants,
          availableCells: validAvailableCells,
          cellSettings: validCellSettings,
        }
      );
    }
  );
}

export function useSetCellSlotsPerCell() {
  return useMutation(api.gardens.mutations.setCellSlotsPerCell).withOptimisticUpdate(
    (localStore, { gardenId, row, col, slotsPerCell: slots }) => {
      const current = localStore.getQuery(api.gardens.queries.getGardenById, { gardenId });
      if (current === undefined) return;

      const clamped = Math.max(1, Math.min(Math.floor(slots), MAX_SLOTS));
      const filtered = (current.cellSettings || []).filter(
        (c) => !(c.row === row && c.col === col)
      );
      const nextCellSettings =
        clamped === DEFAULT_SLOTS ? filtered : [...filtered, { row, col, slotsPerCell: clamped }];
      const nextPlants = current.plants.filter((p) => {
        if (p.row !== row || p.col !== col) return true;
        return p.slot < clamped;
      });

      localStore.setQuery(
        api.gardens.queries.getGardenById,
        { gardenId },
        {
          ...current,
          cellSettings: nextCellSettings,
          plants: nextPlants,
        }
      );
    }
  );
}

export function useSetBulkCellSlotsPerCell() {
  return useMutation(api.gardens.mutations.setBulkCellSlotsPerCell).withOptimisticUpdate(
    (localStore, { gardenId, cells, slotsPerCell: slots }) => {
      const current = localStore.getQuery(api.gardens.queries.getGardenById, { gardenId });
      if (current === undefined) return;

      const clamped = Math.max(1, Math.min(Math.floor(slots), MAX_SLOTS));
      let cellSettings = current.cellSettings || [];
      let plants = current.plants;

      for (const { row, col } of cells) {
        cellSettings = cellSettings.filter((c) => !(c.row === row && c.col === col));
        if (clamped !== DEFAULT_SLOTS) {
          cellSettings = [...cellSettings, { row, col, slotsPerCell: clamped }];
        }
        plants = plants.filter((p) => {
          if (p.row !== row || p.col !== col) return true;
          return p.slot < clamped;
        });
      }

      localStore.setQuery(
        api.gardens.queries.getGardenById,
        { gardenId },
        {
          ...current,
          cellSettings,
          plants,
        }
      );
    }
  );
}

export function useSetAllCellsAvailability() {
  return useMutation(api.gardens.mutations.setAllCellsAvailability).withOptimisticUpdate(
    (localStore, { gardenId, available }) => {
      const current = localStore.getQuery(api.gardens.queries.getGardenById, { gardenId });
      if (current === undefined) return;

      const nextAvailableCells = available
        ? Array.from({ length: current.rows }, (_, row) =>
            Array.from({ length: current.columns }, (_, col) => ({ row, col }))
          ).flat()
        : [];

      localStore.setQuery(
        api.gardens.queries.getGardenById,
        { gardenId },
        { ...current, availableCells: nextAvailableCells }
      );
    }
  );
}
